import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, EmptyState, Loading, StatCard } from "../../components/VB";
import { toast } from "sonner";
import { CheckCircle2, Code2, Palette } from "lucide-react";

// Menu "Pendapatan" di dashboard karyawan — TIDAK ada akun/portal terpisah untuk developer atau
// designer, mereka tetap login sebagai karyawan biasa (satu dashboard yang sama). Delegasinya
// murni lewat Developer.employee_id / Designer.employee_id yang diset admin (lihat Payment
// Developer/Payment Designer → Pengaturan → "Hubungkan ke Karyawan") — halaman ini cuma
// menampilkan section yang relevan berdasarkan /karyawan/pendapatan-status:
//   - terhubung sebagai Developer -> "List Web Project"
//   - terhubung sebagai Designer  -> "List Designer Project"
//   - dua-duanya (jarang, tapi mungkin) -> tab switcher
//   - tidak terhubung sama sekali -> pesan kosong, bukan error
const idr = (n) => "Rp " + Math.round(parseFloat(n || 0)).toLocaleString("id-ID");

const getCurrentPeriode = () => {
  const today = new Date().getDate();
  return today <= 15 ? "15" : "30";
};

export default function Pendapatan() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(null); // "developer" | "designer"

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/karyawan/pendapatan-status");
        setStatus(data);
        setTab(data.is_developer ? "developer" : data.is_designer ? "designer" : null);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <Loading message="Memuat data pendapatan..." />;
  if (!status || (!status.is_developer && !status.is_designer)) {
    return (
      <Window title="Pendapatan">
        <EmptyState message="Anda belum terhubung sebagai developer atau designer manapun. Jika ini seharusnya tidak terjadi, hubungi admin untuk dihubungkan lewat Payment Developer/Payment Designer." />
      </Window>
    );
  }

  const showTabs = status.is_developer && status.is_designer;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {showTabs && (
        <div style={{ display: "flex", gap: 6 }}>
          <button className={`vb-btn ${tab === "developer" ? "vb-btn-primary" : ""}`} onClick={() => setTab("developer")} data-testid="pendapatan-tab-dev">
            <Code2 className="w-3 h-3 inline mr-1" />List Web Project
          </button>
          <button className={`vb-btn ${tab === "designer" ? "vb-btn-primary" : ""}`} onClick={() => setTab("designer")} data-testid="pendapatan-tab-designer">
            <Palette className="w-3 h-3 inline mr-1" />List Designer Project
          </button>
        </div>
      )}
      {tab === "developer" && <RecapSection kind="developer" title="List Web Project" />}
      {tab === "designer" && <RecapSection kind="designer" title="List Designer Project" />}
    </div>
  );
}

function RecapSection({ kind, title }) {
  const [recap, setRecap] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [filterBulan, setFilterBulan] = useState(today.slice(0, 7));
  const [filterPeriode, setFilterPeriode] = useState(getCurrentPeriode);

  const endpoint = kind === "developer" ? "/dev-payment/recap" : "/designer-payment/recap";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, { params: { bulan: filterBulan, periode: filterPeriode } });
      setRecap(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterBulan, filterPeriode]);

  const totalEarning = recap.reduce((s, r) => s + (r.total_earning_idr || 0), 0);
  const totalPaid = recap.reduce((s, r) => s + (r.paid_idr || 0), 0);
  const totalSisa = recap.reduce((s, r) => s + (r.sisa_idr || 0), 0);

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
        <StatCard label="Total Earning" value={idr(totalEarning)} testId={`pendapatan-${kind}-stat-earning`} />
        <StatCard label="Sudah Dibayar" value={idr(totalPaid)} testId={`pendapatan-${kind}-stat-paid`} />
        <StatCard label="Belum Dibayar" value={idr(totalSisa)} testId={`pendapatan-${kind}-stat-sisa`} />
      </div>

      <Window
        title={title}
        actions={
          <>
            <select className="vb-select" style={{ width: 150 }} value={filterPeriode} onChange={(e) => setFilterPeriode(e.target.value)} data-testid={`pendapatan-${kind}-filter-periode`}>
              <option value="15">Periode 1-15</option>
              <option value="30">Periode 16-30/31</option>
              <option value="all">Semua Periode</option>
            </select>
            <input type="month" className="vb-input" style={{ width: 160 }} value={filterBulan} onChange={(e) => setFilterBulan(e.target.value)} data-testid={`pendapatan-${kind}-filter-bulan`} />
          </>
        }
      >
        {loading ? <Loading /> : recap.length === 0 ? <EmptyState message="Belum ada project pada periode/bulan ini. Coba ganti filter di atas." /> : (
          <div style={{ overflowX: "auto" }}>
            <table className="vb-table" data-testid={`pendapatan-${kind}-table`}>
              <thead>
                <tr>
                  <th>Periode (Due 15/30)</th>
                  <th style={{ textAlign: "center" }}>Jml Project</th>
                  <th style={{ textAlign: "right" }}>Total Earning</th>
                  <th style={{ textAlign: "right" }}>Dibayar</th>
                  <th style={{ textAlign: "right" }}>Sisa</th>
                  <th>Status</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {recap.map((r) => (
                  <tr key={r.periode}>
                    <td style={{ fontWeight: 700, color: "#003399" }}>{r.periode}</td>
                    <td style={{ textAlign: "center" }}>{r.count}</td>
                    <td style={{ textAlign: "right" }}>
                      {idr(r.total_earning_idr)}
                      {r.total_earning_gross_idr > r.total_earning_idr && (
                        <div style={{ fontSize: 10, color: "#888" }}>bruto: {idr(r.total_earning_gross_idr)}</div>
                      )}
                    </td>
                    <td style={{ textAlign: "right", color: "#4f8a10" }}>{idr(r.paid_idr)}</td>
                    <td style={{ textAlign: "right", color: "#d8000c", fontWeight: 600 }}>{idr(r.sisa_idr)}</td>
                    <td>
                      {r.status === "Lunas" ? <span className="vb-badge vb-badge-approved"><CheckCircle2 className="w-3 h-3 inline mr-1" />Lunas</span>
                        : r.status === "Sebagian" ? <span className="vb-badge vb-badge-pending">Sebagian</span>
                        : <span className="vb-badge vb-badge-rejected">Pending</span>}
                    </td>
                    <td>
                      <button className="vb-btn" style={{ padding: "2px 6px" }} onClick={() => setDetail(r)} data-testid={`pendapatan-${kind}-detail-${r.periode}`}>Lihat Rincian</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: 10, fontSize: 11, color: "#666" }}>
          Pencatatan pembayaran dilakukan oleh admin. Halaman ini khusus untuk memantau, tidak bisa mengubah data.
        </div>
      </Window>

      <Dialog open={!!detail} onClose={() => setDetail(null)} title={`Rincian Project — Periode ${detail?.periode || ""}`} width={kind === "developer" ? 620 : 560}
        footer={<button className="vb-btn" onClick={() => setDetail(null)}>Tutup</button>}>
        {detail && (
          <table className="vb-table">
            {kind === "developer" ? (
              <>
                <thead><tr><th>Project</th><th>Domain</th><th>Type</th><th>Ext</th><th style={{ textAlign: "right" }}>Rate Web</th><th style={{ textAlign: "right" }}>Rate Domain</th><th>Rating</th><th style={{ textAlign: "right" }}>Earning</th></tr></thead>
                <tbody>
                  {detail.projects.map((p, i) => {
                    const rEff = p.rating_effective ?? 5;
                    const pct = p.pay_percent ?? 100;
                    return (
                      <tr key={i}>
                        <td><strong>{p.nama_project}</strong></td>
                        <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{p.domain}</td>
                        <td>{p.type_web}</td>
                        <td><span className="vb-badge vb-badge-izin">{p.ext}</span></td>
                        <td style={{ textAlign: "right" }}>{idr(p.rate_web_idr)}</td>
                        <td style={{ textAlign: "right" }}>{idr(p.rate_domain_idr)}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <span style={{ color: "#f5a623", letterSpacing: -1 }}>{"★".repeat(rEff)}</span>
                          <span style={{ color: "#ddd", letterSpacing: -1 }}>{"★".repeat(5 - rEff)}</span>
                          {pct < 100 && <div style={{ color: "#7c3aed", fontWeight: 700, fontSize: 10 }}>{pct}%</div>}
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 700, color: "#003399" }}>
                          {idr(p.earning_idr)}
                          {pct < 100 && (
                            <div style={{ fontSize: 10, color: "#888", textDecoration: "line-through" }}>{idr(p.earning_idr_gross)}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </>
            ) : (
              <>
                <thead><tr><th>Project</th><th>Domain</th><th>Type</th><th style={{ textAlign: "right" }}>Rate Design</th><th style={{ textAlign: "right" }}>Earning</th></tr></thead>
                <tbody>
                  {detail.projects.map((p, i) => (
                    <tr key={i}>
                      <td><strong>{p.nama_project}</strong></td>
                      <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{p.domain}</td>
                      <td>{p.type_web}</td>
                      <td style={{ textAlign: "right" }}>{idr(p.rate_design_idr)}</td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: "#003399" }}>{idr(p.earning_idr)}</td>
                    </tr>
                  ))}
                </tbody>
              </>
            )}
          </table>
        )}
      </Dialog>
    </>
  );
}
